import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { BarStackHorizontal } from '@visx/shape';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';

import { thousandFormat2 } from '../../../../Utils/Functions';
import {
  thinAxis,
  barThinHeight,
} from '../../../../Assets/js/constant';

let tooltipTimeout;

const BottomChart = ({
  detailData,
  filterName,
  selectedTopItems,
  handleFilter,
}) => {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedBarObjs, setSelectedBarObjs] = useState([]);
  const [data, setData] = useState([]);
  const [maxData, setMaxData] = useState(0);
  const [minData, setMinData] = useState(0);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (detailData) {
      prepareData();
    }
  }, [detailData, filterName, selectedTopItems]);

  const prepareData = useCallback(() => {
    let newData = [],
      newMaxData = 0,
      newMinData = 0;

    if (detailData) {
      var _item = {};

      detailData.forEach((item) => {
        if (item) {
          _item = {};
          if (item['ProjectId']) _item['ProjectId'] = item['ProjectId'];
          else _item['ProjectId'] = '';

          if (item['ProjectName']) _item['ProjectName'] = item['ProjectName'];
          else _item['ProjectId'] = '';

          if (item['PDirector']) _item['PDirector'] = item['PDirector'];
          else _item['ProjectId'] = '';

          if (item['Project Type'])
            _item['Project Type'] = item['Project Type'];
          else _item['Project Type'] = '';

          _item['Project Source 1'] = item['Project Source 1'] || '';
          _item['Project Source 2'] = item['Project Source 2'] || '';

          if (item['IF12Months']) _item['IF12Months'] = item['IF12Months'];
          else _item['IF12Months'] = 0;

          if (item['IFGT12Months'])
            _item['IFGT12Months'] = item['IFGT12Months'];
          else _item['IFGT12Months'] = 0;

          var isInFilter = false;
          if (selectedTopItems) {
            selectedTopItems.forEach((filter) => {
              if (filterName === 'Director')
                if (filter === _item['PDirector']) isInFilter = true;

              if (filterName === 'Project Type')
                if (filter === _item['Project Type']) isInFilter = true;

              if (filterName === 'Project Source 1')
                if (filter === _item['Project Source 1']) isInFilter = true;

              if (filterName === 'Project Source 2')
                if (filter === _item['Project Source 2']) isInFilter = true;
            });
          }

          if (
            isInFilter ||
            !selectedTopItems ||
            selectedTopItems.length === 0
          ) {
            newData.push(_item);
            newMaxData = Math.max(
              newMaxData,
              _item['IF12Months'] + _item['IFGT12Months'],
            );
          }
        }
      });
    }
    // data = []; // error test

    setData(newData);
    setMaxData(newMaxData);
    setMinData(newMinData);
  }, [detailData, filterName, selectedTopItems]);

  const getColor = useCallback((bar) => {
    const activeColor = bar.key === 'IF12Months' ? '#27aae1' : '#1c75bc';
    const disableColor = bar.key === 'IFGT12Months' ? '#ccdce9' : '#cee6f0';

    let index = -1;
    if (selectedBarObjs && selectedBarObjs.length !== 0) {
      selectedBarObjs.forEach((item) => {
        if (
          item.bar.data.ProjectName === bar.bar.data.ProjectName &&
          item.bar.key === bar.bar.key
        )
          index++;
      });
    } else {
      return activeColor;
    }

    if (index >= 0) return activeColor;

    return disableColor;
  }, [selectedBarObjs]);

  const handleElementSelect = useCallback((event, element) => {
    // Empty function as in original
  }, []);

  const getShortName = useCallback((name) => {
    var sortname = '';
    if (name) {
      for (let i = 0; i < name.length; i++) {
        if (name[i] >= 'A' && name[i] <= 'Z') sortname += name[i];
      }
    }
    return sortname;
  }, []);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedBarObjs;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBarObjs = selectedBarObjs.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedBarObjs.push(bar);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedBarObjs.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedBars = [];
        _selectedBarObjs = [];
      } else {
        _selectedBars = [event.target];
        _selectedBarObjs = [bar];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);
    setSelectedBarObjs(_selectedBarObjs);
  }, [selectedBars, selectedBarObjs]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    setSelectedBarObjs([]);

    handleFilter({
      selectedTopItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {},
      top,
      left;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      top = event.clientY - 250;
      left = event.clientX - 480;
      data = bar;
    } else {
      top = event.clientY - 250;
      left = event.clientX;
      data['forecast'] = bar + 100;
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);

      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  const width = window.innerWidth - 16 > 0 ? window.innerWidth - 16 : 0;
  const ySelector = (item) => item.ProjectName;

  const sortedData = data
    .slice()
    .sort(
      (a, b) =>
        b.IF12Months + b.IFGT12Months -
        (a.IF12Months + a.IFGT12Months),
    );

  var numTicks = 5;
  let currentMaxData = maxData;
  let currentMinData = minData;
  if (currentMaxData === 0 && currentMinData === 0) {
    currentMaxData = 10;
    numTicks = 2;
  }

  // scales
  const yScale = scaleBand()
    .rangeRound([5, 0])
    .domain(sortedData.map(ySelector))
    .padding(0.2);

  const keys = ['IF12Months', 'IFGT12Months'];

  const color = scaleOrdinal({
    domain: keys,
    range: ['#27aae1', '#1c75bc'],
  });

  let projectIdLengths = [],
    maxProjectIdLength = 0,
    projectNameLengths = [],
    maxProjectNameLength = 0,
    projectTypeLengths = [],
    maxProjectTypeLength = 0;
  sortedData.forEach((d) => {
    projectIdLengths.push(d.ProjectId?.length);
    projectNameLengths.push(d.ProjectName?.length);
    projectTypeLengths.push(d['Project Type']?.length);
  });
  if (projectIdLengths.length > 0) {
    maxProjectIdLength = Math.max(...projectIdLengths);
    maxProjectNameLength = Math.max(...projectNameLengths);
    maxProjectTypeLength = Math.max(...projectTypeLengths);
  }

  let bottomHeight = window.innerWidth >= 1280 ? 23 * 24 : 23 * 22 - 2;

  let scaleWidth =
    width -
      maxProjectIdLength * 8 -
      (maxProjectNameLength + maxProjectTypeLength) * 7.2 -
      70 >
    0
      ? width -
        maxProjectIdLength * 8 -
        (maxProjectNameLength + maxProjectTypeLength) * 7.2 -
        70
      : 0;
  const xScale = scaleLinear({
    range: [0, scaleWidth],
    domain: [0, currentMaxData * 1.1],
  });

  return (
    <div style={{
      height: '67vh',
    }}>
      <div className="subtitle mb-10" style={{ marginTop: 6 }}>
        By Project
      </div>

      {sortedData.length > 0 ? (
        <Grid
          container
          style={{ maxHeight: bottomHeight, overflowY: 'auto', fontSize: 12 }}
          id="wih_bottom"
        >
          {sortedData.map((item, i) => {
            const yMax = barThinHeight - 5;
            const _item = [];
            _item.push(item);

            return (
              <Grid
                container
                key={i}
                sx={{
                  borderTop: 'solid 1px #a9a9a9',
                }}
                onClick={(event) => handleElementSelect(event, item)}
              >
                <div
                  style={{
                    borderRight: 'solid 1px #a9a9a9',
                    display: 'flex',
                    alignItems: 'center',
                    width: maxProjectIdLength * 8
                  }}
                >
                  {item.ProjectId}
                </div>
                <div
                  style={{
                    borderRight: 'solid 1px #a9a9a9',
                    display: 'flex',
                    alignItems: 'center',
                    width: maxProjectNameLength * 7.2
                  }}
                >
                  &nbsp;{item.ProjectName}
                </div>
                <div
                  style={{
                    borderRight: 'solid 1px #a9a9a9',
                    display: 'flex',
                    alignItems: 'center',
                    width: 30
                  }}
                >
                  &nbsp;{getShortName(item.PDirector)}
                </div>
                <div
                  style={{
                    borderRight: 'solid 1px #a9a9a9',
                    display: 'flex',
                    alignItems: 'center',
                    width: maxProjectTypeLength * 7.2 + 5
                  }}
                >
                  &nbsp;{item['Project Type']}
                </div>

                <div>
                  <svg width={scaleWidth} height={yMax}>
                    <rect
                      x={0}
                      y={0}
                      width={scaleWidth}
                      height={yMax}
                      fill="transparent"
                      onClick={deSelectAll}
                    />
                    <BarStackHorizontal
                      data={_item}
                      keys={keys}
                      y={ySelector}
                      xScale={xScale}
                      yScale={yScale}
                      color={color}
                    >
                      {(barStacks) =>
                        barStacks.map((barStack) =>
                          barStack.bars.map((bar) => (
                            <rect
                              key={`barstack-horizontal-${barStack.index}-${bar.index}`}
                              x={bar.x}
                              y={bar.y}
                              height={yMax}
                              width={Math.max(0, bar.width)}
                              fill={getColor(bar)}
                              onClick={(event) => handleBar(event, bar)}
                              onMouseLeave={() => hideTooltipHandler()}
                              onMouseMove={(event) =>
                                showTooltipHandler(event, xScale, bar)
                              }
                              onTouchEnd={() => hideTooltipHandler()}
                              onTouchMove={(event) =>
                                showTooltipHandler(event, xScale, bar)
                              }
                            />
                          )),
                        )
                      }
                    </BarStackHorizontal>
                  </svg>
                </div>
              </Grid>
            );
          })}
        </Grid>
      ) : null}

      {sortedData.length > 0 ? (
        <Grid
          container
          style={{
            height: barThinHeight + 4,
            borderTop: 'solid 1px #a9a9a9',
            overflowY: 'auto',
          }}
          onClick={deSelectAll}
        >
          <svg width={width} height={barThinHeight}>
            <rect
              x={0}
              y={0}
              width={width}
              height={barThinHeight}
              fill="transparent"
            />

            <AxisBottom
              left={
                maxProjectIdLength * 8 +
                (maxProjectNameLength + maxProjectTypeLength) * 7.2 +
                35
              }
              scale={xScale}
              hideAxisLine={true}
              stroke="black"
              numTicks={numTicks}
              tickStroke="black"
              tickFormat={(value, index) => value / 1000}
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'middle',
                dy: '0.2em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps}>
                  $
                  {Math.abs(formattedValue) < 1000
                    ? `${formattedValue}K`
                    : `${formattedValue / 1000}M`}
                </text>
              )}
            />
          </svg>
        </Grid>
      ) : (
        <div style={thinAxis} onClick={deSelectAll} align="center">
          <p>No Data</p>
        </div>
      )}

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop + 50}
          left={tooltipLeft}
        >
          {tooltipData.isBar ? (
            <div className="bkg-white color-black">
              <div className="pdv-10">
                <strong>
                  {tooltipData.bar.data.ProjectId}{' '}
                  {tooltipData.bar.data.ProjectName}
                </strong>
              </div>
              <div className="ft-12">
                Project Type:
                <strong> {tooltipData.bar.data['Project Type']}</strong>
              </div>
              <div className="ft-12">&nbsp;&nbsp;</div>
              <div className="ft-12">
                Contract remaining:
                <strong>
                  {thousandFormat2(
                    tooltipData.bar.data.IF12Months +
                      tooltipData.bar.data.IFGT12Months,
                  )}
                </strong>
              </div>
              <div className="ft-12">
                To invoice next 12 months:
                <strong>
                  {thousandFormat2(tooltipData.bar.data.IF12Months)}
                </strong>
              </div>
              <div className="ft-12">
                To invoice after 12 months:
                <strong>
                  {thousandFormat2(tooltipData.bar.data.IFGT12Months)}
                </strong>
              </div>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </div>
  );
};

BottomChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  filterName: PropTypes.string.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default BottomChart;
